import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import { convexClient } from '../convexClient'
import { api, coerceSong, sanitizeYouTubeUrl } from '../lib/convexHelpers'
import { songsAPI } from '../services/api'
import toast from 'react-hot-toast'
import { getYouTubeSearchErrorMessage } from '../utils/searchErrors'

/**
 * Search store - handles song search (database and YouTube)
 * Manages search state, results, and YouTube URL submission
 *
 * Database searches go directly to Convex.
 * YouTube searches go through the backend API (YouTube API proxy).
 */
const useSearchStore = create((set, get) => ({
  // Database search state
  songs: [],
  searchResults: [],
  isSearching: false,

  // YouTube search state
  youtubeSearchResults: [],
  isSearchingYouTube: false,
  hasSearchedYouTube: false,

  // Actions

  /**
   * Search songs in the Convex database
   */
  searchSongs: async (query) => {
    set({ isSearching: true })
    try {
      let results
      if (query && query.trim()) {
        results = await convexClient.query(api.songs.search, { query })
      } else {
        results = await convexClient.query(api.songs.list, {})
      }
      set({ searchResults: (results || []).map(coerceSong) })
    } catch (error) {
      toast.error('Failed to search songs')
    } finally {
      set({ isSearching: false })
    }
  },

  /**
   * Search YouTube for videos (via backend API)
   */
  searchYouTube: async (query) => {
    set({ isSearchingYouTube: true, hasSearchedYouTube: true })
    try {
      const results = await songsAPI.searchYouTube(query)
      set({ youtubeSearchResults: results })
    } catch (error) {
      toast.error(getYouTubeSearchErrorMessage(error), { duration: 6000 })

      console.error('YouTube search error:', error)
      set({ youtubeSearchResults: [] })
    } finally {
      set({ isSearchingYouTube: false })
    }
  },

  /**
   * Submit a YouTube URL for processing
   * Returns the processing job or existing duplicate
   */
  submitYouTubeUrl: async (url, addedBy = 'anonymous', roomCode = null, onJobCreated = null) => {
    const sanitizedUrl = sanitizeYouTubeUrl(url)

    // Check for duplicates via Convex
    const duplicate = await convexClient.query(api.processingJobs.checkDuplicate, { youtubeUrl: sanitizedUrl })
    if (duplicate) {
      toast('This video is already being processed', {
        icon: '⚠️',
        style: {
          background: '#fbbf24',
          color: '#92400e',
        },
      })
      return duplicate
    }

    // Submit via backend (triggers processing pipeline)
    const job = await songsAPI.submitYouTubeUrl(sanitizedUrl, addedBy, roomCode)

    if (onJobCreated) {
      onJobCreated(job)
    }

    toast.success('Song submitted for processing and will be queued once ready')
    return job
  },

  /**
   * Load all processed songs from Convex database
   */
  loadSongs: async () => {
    try {
      const songs = await convexClient.query(api.songs.list, {})
      set({ songs: (songs || []).map(coerceSong) })
    } catch (error) {
      toast.error('Failed to load songs')
    }
  },

  /**
   * Reset YouTube search state only
   */
  resetYouTubeSearch: () => {
    set({
      youtubeSearchResults: [],
      isSearchingYouTube: false,
      hasSearchedYouTube: false
    })
  },

  /**
   * Clear search results
   */
  clearSearchResults: () => {
    set({
      searchResults: [],
      youtubeSearchResults: [],
      hasSearchedYouTube: false
    })
  },

  /**
   * Reset store state
   */
  reset: () => {
    set({
      songs: [],
      searchResults: [],
      isSearching: false,
      youtubeSearchResults: [],
      isSearchingYouTube: false,
      hasSearchedYouTube: false
    })
  }
}))

// Selector hooks for granular subscriptions

export const useSearchResults = () => useSearchStore(
  state => ({
    searchResults: state.searchResults,
    isSearching: state.isSearching
  }),
  shallow
)

export const useYouTubeSearch = () => useSearchStore(
  state => ({
    youtubeSearchResults: state.youtubeSearchResults,
    isSearchingYouTube: state.isSearchingYouTube,
    hasSearchedYouTube: state.hasSearchedYouTube
  }),
  shallow
)

export const useSongs = () => useSearchStore(state => state.songs)

export default useSearchStore
